import { Tag } from 'antd'
import dayjs from 'dayjs'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
  LINK_TEMPLATE_ASSET,
} from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import { COMPONENT_TYPE_COLORS } from 'features/drilldown/components/table/SharedTableComponents'
import max from 'lodash/max'
import uniq from 'lodash/uniq'
import { FC, useMemo } from 'react'
import { GeckoComponentModelType } from 'types'

type UnitTablePanelData = DrilldownTablePanelData

export const UNIT_TABLE_COLUMN_DEFS: ReadonlyArray<DrilldownTableColumnDef> = [
  {
    title: 'Asset',
    field: 'unit.name',
    copyField: 'unit.id',
    linkTemplate: LINK_TEMPLATE_ASSET,
  },
  {
    title: 'Type',
    field: 'asset-model.type',
  },
]

const LastInspectionDateCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const lastInspectionDate = useMemo(
    () =>
      max(
        entries
          .filter((e) => e.type === 'inspection')
          .map((e) => e.data.date as string | undefined)
          .filter((date) => !!date)
          .map((dateStr) => dayjs(dateStr))
          .filter((date) => date.isValid())
          .map((date) => date.format('YYYY-MM-DD'))
      ),
    [entries]
  )
  return lastInspectionDate ?? <></>
}

const AssetModelComponentsCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const types = useMemo(
    () =>
      uniq(
        entries
          .filter((e) => e.type === 'asset-model')
          .map(
            (e) =>
              e.data.components as Map<
                string,
                { type: GeckoComponentModelType }
              >
          )
          .flatMap((components) => [...components.values()])
          .map((component) => component.type)
      ).toSorted(),
    [entries]
  )

  return (
    <>
      {types.map((t) => (
        <Tag key={t} color={COMPONENT_TYPE_COLORS[t]}>
          {t}
        </Tag>
      ))}
    </>
  )
}

export const UNIT_TABLE_EXTRA_COLS: ReadonlyArray<DrilldownTableExtraColumn> = [
  {
    id: 'col_last_inspection_date',
    name: 'Last Inspection',
    linked_types: ['inspection'],
    render: (props) => <LastInspectionDateCol {...props} />,
  },
  {
    id: 'col_component_model_types',
    name: 'Component Types',
    linked_types: ['asset-model'],
    render: (props) => <AssetModelComponentsCol {...props} />,
  },
]

export const DEFAULT_UNIT_TABLE_PANEL_DATA: UnitTablePanelData = {
  ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  title: 'Assets',
  isPinned: true,
  columns: UNIT_TABLE_COLUMN_DEFS,
  groupByType: 'unit',
  enabledExtraColumnIds: UNIT_TABLE_EXTRA_COLS.map((col) => col.id),
}
